import { useEffect, useState } from 'react';
import { ACTS } from './useDeckState';
import { T, FONT } from './theme';

interface Props {
  act: number;
  go: (n: number) => void;
}

/**
 * Top act-tab bar: 7 tabs with a sliding underline. Each tab starts with a
 * "to-visit" dot; the dot is removed once the client has visited that section
 * (tracked cumulatively, so it stays gone even if they navigate back).
 */
export function HubBar({ act, go }: Props) {
  // Seed with the section we land on (Overview) so it isn't dotted from the
  // start — you've "arrived" there. Every other section keeps its dot until
  // visited.
  const [visited, setVisited] = useState<Set<number>>(() => new Set([act]));

  useEffect(() => {
    setVisited((prev) => {
      if (prev.has(act)) return prev;
      const next = new Set(prev);
      next.add(act);
      return next;
    });
  }, [act]);

  return (
    <div
      style={{
        background: T.card,
        borderBottom: `1px solid ${T.grayLine}`,
        padding: '0 28px',
        display: 'flex',
        alignItems: 'stretch',
        height: 54,
        flexShrink: 0,
        overflowX: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 2, margin: '0 auto', maxWidth: 1080, width: '100%' }}>
        {ACTS.map((a, i) => {
          const active = i === act;
          const seen = visited.has(i);
          // Dot marks a section the client hasn't been to yet.
          const showDot = !seen;
          return (
            <button
              key={a.id}
              onClick={() => go(i)}
              style={{
                position: 'relative',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '0 16px',
                fontFamily: FONT,
                fontSize: 13.5,
                fontWeight: active ? 600 : 500,
                color: active ? T.navy : seen ? '#5E6B7C' : T.gray,
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                whiteSpace: 'nowrap',
                transition: 'color .18s',
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.color = 'var(--color-dark)';
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.color = seen ? '#5E6B7C' : 'var(--color-gray)';
              }}
            >
              {showDot && <span style={{ width: 6, height: 6, borderRadius: 3, background: T.teal, flexShrink: 0 }} />}
              {a.label}
              {/* sliding underline indicator */}
              <span
                style={{
                  position: 'absolute',
                  left: 8,
                  right: 8,
                  bottom: 0,
                  height: 2.5,
                  borderRadius: '3px 3px 0 0',
                  background: active ? T.teal : 'transparent',
                  transition: 'background .2s',
                }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
